/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { User, ViewState, Transaction } from '../../types';
import { ArrowLeft, Edit, MoreVertical, Shield, BarChart2, CheckSquare, FileText } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface UserDetailsViewProps {
    user: User | null;
    onNavigate: (view: ViewState) => void;
}

export const UserDetailsView: React.FC<UserDetailsViewProps> = ({ user, onNavigate }) => {
    const [activeTab, setActiveTab] = useState<'activity' | 'permissions'>('activity');

    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-stone-400">
                <p>No user selected.</p>
                <Button onClick={() => onNavigate('management')} className="mt-4">Go Back</Button>
            </div>
        );
    }
    
    // Mock data for user activity
    // FIX: Corrected transaction data to match the `Transaction` type. `date` is now a number (timestamp), and `chain` and `txHash` are included.
    const recentActivity: Transaction[] = [
        { id: 'TX_1029', type: 'payment', amount: 45.00, status: 'completed', date: new Date('2024-11-20').getTime(), asset: 'USDC', chain: 'SOL', txHash: 'mock_tx_hash_1_sol_user_details' },
        { id: 'ESC_8921', type: 'escrow', amount: 1500.00, status: 'pending', date: new Date('2024-11-18').getTime(), asset: 'SUI', chain: 'SUI', txHash: 'mock_tx_hash_2_sui_user_details' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => onNavigate('management')} className="!p-2 h-auto">
                    <ArrowLeft size={16} />
                </Button>
                <div>
                    <h2 className="font-serif text-3xl text-stone-900 dark:text-white">{user.name}</h2>
                    <p className="text-stone-500 text-sm">User details and access management.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Profile Card */}
                <div className="lg:col-span-1 space-y-6">
                    <Card>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-24 h-24 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center text-4xl font-serif font-bold text-stone-800 dark:text-white mb-4">
                                {user.name.charAt(0)}
                            </div>
                            <h3 className="text-xl font-bold text-stone-900 dark:text-white">{user.name}</h3>
                            <p className="text-sm text-stone-500">{user.email}</p>
                            <div className={`mt-4 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${user.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                                {user.status}
                            </div>
                        </div>

                        <div className="border-t border-stone-100 dark:border-white/10 my-6"></div>

                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-stone-500">Role:</span>
                                <span className="font-bold capitalize text-stone-900 dark:text-white">{user.role}</span>
                            </div>
                             <div className="flex justify-between">
                                <span className="text-stone-500">Member Since:</span>
                                <span className="font-bold text-stone-900 dark:text-white">{user.created_at}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-stone-500">Last Active:</span>
                                <span className="font-bold text-stone-900 dark:text-white">Just now</span>
                            </div>
                        </div>

                         <div className="border-t border-stone-100 dark:border-white/10 my-6"></div>
                         
                         <div className="flex gap-3">
                             <Button variant="outline" className="w-full">Suspend User</Button>
                             <Button variant="secondary" className="w-full">Reset Password</Button>
                         </div>
                    </Card>
                </div>

                {/* Right Column: Tabs */}
                <div className="lg:col-span-2">
                    <Card>
                        <div className="flex border-b border-stone-200 dark:border-white/10 mb-6">
                            <button onClick={() => setActiveTab('activity')} className={`px-4 py-3 text-sm font-bold ${activeTab === 'activity' ? 'text-stone-900 dark:text-white border-b-2 border-nobel-gold' : 'text-stone-500'}`}>
                                <BarChart2 size={16} className="inline-block mr-2"/> Activity
                            </button>
                             <button onClick={() => setActiveTab('permissions')} className={`px-4 py-3 text-sm font-bold ${activeTab === 'permissions' ? 'text-stone-900 dark:text-white border-b-2 border-nobel-gold' : 'text-stone-500'}`}>
                                <CheckSquare size={16} className="inline-block mr-2"/> Permissions
                            </button>
                        </div>
                        
                        {activeTab === 'activity' && (
                            <div className="space-y-4">
                                {recentActivity.map(tx => (
                                    <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-stone-50 dark:bg-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center">
                                                <FileText size={14}/>
                                            </div>
                                            <div>
                                                <div className="font-bold text-sm text-stone-900 dark:text-white capitalize">{tx.type} - {tx.id}</div>
                                                <div className="text-xs text-stone-500">{new Date(tx.date).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono font-bold text-sm">${tx.amount.toFixed(2)}</div>
                                            <div className="text-xs text-stone-500">{tx.asset}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'permissions' && (
                            <div className="space-y-4">
                                {['Can issue refunds', 'Can export data', 'Can manage terminals', 'Can view analytics'].map(perm => (
                                     <div key={perm} className="flex items-center justify-between p-3 rounded-lg bg-stone-50 dark:bg-white/5">
                                        <label htmlFor={perm} className="font-medium text-sm text-stone-900 dark:text-white">{perm}</label>
                                        <input id={perm} type="checkbox" className="h-4 w-4 rounded accent-nobel-gold" defaultChecked={Math.random() > 0.5} />
                                     </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};